/**
 * Formats a binary value (0/1) as Yes/No for display purposes
 */
export const formatBinaryValue = (value: number | string): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value

  if (numValue === 1) {
    return "Yes"
  } else if (numValue === 0) {
    return "No"
  }

  // Return original value if not binary
  return String(value)
}

/**
 * Checks if a value is binary (0 or 1)
 */
export const isBinaryValue = (value: number | string): boolean => {
  const numValue = typeof value === "string" ? parseFloat(value) : value
  return numValue === 0 || numValue === 1
}

/**
 * Formats a numeric value for chart display
 * For binary metrics:
 *   - Shows "Yes"/"No" for exact 0/1 values (only for data points, not axis ticks)
 *   - Shows percentages (e.g. "40%") for averaged values between 0-1
 * @param value - The numeric value to format
 * @param isBinary - Whether this metric is known to be binary
 * @param precision - Number of decimal places for non-binary values (default: auto)
 * @param aggregationType - The type of aggregation being used (affects binary formatting)
 * @param isAxisTick - Whether this is formatting an axis tick value (vs. a data point)
 */
export const formatChartValue = (
  value: number | string,
  isBinary = false,
  precision?: number,
  aggregationType?: string,
  isAxisTick = false,
): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value

  // Special handling for binary metrics (Yes/No responses)
  if (isBinary) {
    // For axis ticks, always show as percentage for averaged binary metrics
    if (
      isAxisTick &&
      (aggregationType === "avg" || aggregationType === "average")
    ) {
      // Ensure clean percentage values for axis ticks
      const percentage = Math.round(numValue * 100)
      // Clamp to valid range
      const clampedPercentage = Math.max(0, Math.min(100, percentage))
      return `${clampedPercentage}%`
    }

    // For data points (not axis ticks): Exact 0 or 1 can show as Yes/No
    if (!isAxisTick && (numValue === 0 || numValue === 1)) {
      return formatBinaryValue(value)
    }

    // Averaged binary metrics: show as percentage of Yes responses
    // e.g., 0.4 → "40%" (meaning 40% of responses were Yes)
    if (aggregationType === "avg" || aggregationType === "average") {
      const percentage = Math.round(numValue * 100)
      // Clamp to valid range for safety
      const clampedPercentage = Math.max(0, Math.min(100, percentage))
      return `${clampedPercentage}%`
    }

    // Any other decimal between 0-1: show as percentage
    if (numValue >= 0 && numValue <= 1) {
      const percentage = Math.round(numValue * 100)
      const clampedPercentage = Math.max(0, Math.min(100, percentage))
      return `${clampedPercentage}%`
    }
  }

  if (precision !== undefined) {
    return numValue.toFixed(precision)
  }

  // Auto-precision based on value magnitude
  if (Math.abs(numValue) < 0.01) {
    return numValue.toFixed(3)
  } else if (Math.abs(numValue) < 1) {
    return numValue.toFixed(2)
  } else if (Math.abs(numValue) < 100) {
    return numValue.toFixed(1)
  }
  return Math.round(numValue).toString()
}

/**
 * Formats a numeric value for tooltip display
 * Only shows decimal places when the value is not a whole number
 * @param value - The numeric value to format
 * @returns Formatted string with decimals only when necessary
 */
export const formatTooltipValue = (value: number): string => {
  // Check if the value is a whole number
  if (Number.isInteger(value)) {
    return value.toString()
  }

  // For non-whole numbers, use the same logic as formatChartValue
  if (Math.abs(value) < 0.01) {
    return value.toFixed(3)
  } else if (Math.abs(value) < 1) {
    return value.toFixed(2)
  } else if (Math.abs(value) < 100) {
    return value.toFixed(1)
  }
  return Math.round(value).toString()
}
